import { useContext, useRef, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Fab,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  getSubscriptionAvailability,
  getGetSubscriptionsQueryKey,
  useAdminRevoke,
  useAdminSubscribe,
  useGetSubscriptions,
  useRevoke,
  useSubscribe,
} from '@/generated/client/etransport/etransport';
import type {
  ETransportCustomerSubscriptionRequestDto,
  ETransportSubscriptionAvailabilityDto,
  MinimalCustomer,
} from '@/generated/model';
import { ctxUser } from '@/contexts/UserContext';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';
import { AddSubscriptionForm, type AddSubscriptionResult } from './AddSubscriptionForm';
import { SubscriptionCard } from './SubscriptionCard';
import type { SubscriptionActionResult } from './SubscriptionCard';
import { PlutoClientException } from '@/PlutoClientException';
import { useNotificationStore } from '@/stores/notificationstore';
import {
  filterSubscriptions,
  getSubscriptionUnavailableReasonKey,
  type SubscriptionStatusFilter,
} from './subscriptionUtils';

type CustomerOption = Pick<MinimalCustomer, 'id' | 'name'>;

export function SubscriptionsPanel({
  customers,
  selectedCustomerId,
  isLoadingCustomers,
  customersLoadError,
  onCustomerChange,
  onRetryCustomers,
  onClose,
  onFreightsChanged,
}: {
  customers: CustomerOption[];
  selectedCustomerId: number | undefined;
  isLoadingCustomers: boolean;
  customersLoadError: boolean;
  onCustomerChange: (customerId: number | undefined) => void;
  onRetryCustomers: () => void;
  onClose: () => void;
  onFreightsChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const userCtx = useContext(ctxUser);
  const queryClient = useQueryClient();
  const showNotification = useNotificationStore((state) => state.showNotification);
  const isAdmin = userCtx?.user.isSuperAdmin ?? false;
  const [addSubscriptionOpen, setAddSubscriptionOpen] = useState(false);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [actionError, setActionError] = useState<string>();
  const [subscriptionSearch, setSubscriptionSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<SubscriptionStatusFilter>('all');
  const addButtonRef = useRef<HTMLButtonElement>(null);

  const selectedCustomer =
    isLoadingCustomers || customersLoadError ? undefined : customers.find((item) => item.id === selectedCustomerId);
  const effectiveCustomerId = selectedCustomer?.id;
  const subscriptionParams = { customerId: effectiveCustomerId ?? 0 };
  const subscriptionsQuery = useGetSubscriptions(subscriptionParams, {
    query: { enabled: effectiveCustomerId !== undefined },
  });
  const subscribeMutation = useSubscribe();
  const adminSubscribeMutation = useAdminSubscribe();
  const revokeMutation = useRevoke();
  const adminRevokeMutation = useAdminRevoke();

  const isMutating =
    subscribeMutation.isPending ||
    adminSubscribeMutation.isPending ||
    revokeMutation.isPending ||
    adminRevokeMutation.isPending;

  async function refreshSubscriptions() {
    await queryClient.invalidateQueries({ queryKey: getGetSubscriptionsQueryKey(subscriptionParams) });
  }

  async function refreshAfterMutation() {
    await Promise.all([refreshSubscriptions(), onFreightsChanged()]);
  }

  async function refreshSubscriptionsSafely() {
    try {
      await refreshSubscriptions();
    } catch (error) {
      console.error('Error refreshing customer VAT subscriptions:', error);
    }
  }

  async function refreshAfterStateChange() {
    try {
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error refreshing E-Transport data after a subscription state change:', error);
    }
  }

  async function getLatestSubscriptionAvailability(subscriptionVat: string) {
    if (effectiveCustomerId === undefined) return undefined;
    try {
      const response = await getSubscriptionAvailability({
        customerId: effectiveCustomerId,
        vat: subscriptionVat,
      });
      return response.data;
    } catch (error) {
      console.error('Error checking the latest customer VAT subscription state:', error);
      return undefined;
    }
  }

  function getStateChangedMessage(availability: ETransportSubscriptionAvailabilityDto) {
    return t('etransport.subscriptions.feedback.statechanged', {
      reason: t(getSubscriptionUnavailableReasonKey(availability.unavailableReason)),
    });
  }

  function showActiveSubscriptionNotification(type: 'subscribed' | 'reactivated', subscriptionVat: string) {
    showNotification(
      'success',
      t(`etransport.subscriptions.feedback.${type}`, {
        customer: selectedCustomerLabel ?? String(effectiveCustomerId),
        vat: subscriptionVat,
      }),
      undefined,
      10
    );
  }

  function resetFeedback() {
    setActionError(undefined);
  }

  async function subscribeCustomer(subscriptionVat: string, customerId: number) {
    const request: ETransportCustomerSubscriptionRequestDto = {
      vat: subscriptionVat,
      customerId,
    };
    if (isAdmin) {
      await adminSubscribeMutation.mutateAsync({ data: request });
    } else {
      await subscribeMutation.mutateAsync({ data: request });
    }
  }

  async function handleReactivate(subscriptionVat: string): Promise<SubscriptionActionResult> {
    if (effectiveCustomerId === undefined || subscriptionVat.trim() === '') {
      return { status: 'error', message: t('etransport.subscriptions.feedback.actionerror') };
    }
    resetFeedback();
    try {
      await subscribeCustomer(subscriptionVat.trim(), effectiveCustomerId);
      await refreshAfterMutation();
      showActiveSubscriptionNotification('reactivated', subscriptionVat);
      return { status: 'success' };
    } catch (error) {
      console.error('Error reactivating customer VAT subscription:', error);
      const latestAvailability = await getLatestSubscriptionAvailability(subscriptionVat);
      if (latestAvailability?.state === 'ACTIVE') {
        await refreshAfterStateChange();
        showActiveSubscriptionNotification('reactivated', latestAvailability.vat ?? subscriptionVat);
        return { status: 'success' };
      }
      if (latestAvailability && (latestAvailability.state !== 'REVOKED' || !latestAvailability.canReactivate)) {
        await refreshAfterStateChange();
        return { status: 'error', message: getStateChangedMessage(latestAvailability) };
      }
      await refreshSubscriptionsSafely();
      return { status: 'error', message: t('etransport.subscriptions.feedback.actionerror') };
    }
  }

  async function handleAddSubscription(subscriptionVat: string): Promise<AddSubscriptionResult> {
    if (effectiveCustomerId === undefined) {
      return { status: 'error', message: t('etransport.subscriptions.feedback.actionerror') };
    }
    resetFeedback();
    try {
      const availabilityResponse = await getSubscriptionAvailability({
        customerId: effectiveCustomerId,
        vat: subscriptionVat,
      });
      const availability = availabilityResponse.data;
      if (availability.state === 'ACTIVE') {
        await refreshAfterMutation();
        showActiveSubscriptionNotification('subscribed', availability.vat ?? subscriptionVat);
        return { status: 'success' };
      }
      if (availability.state === 'REVOKED' && availability.canReactivate) {
        return {
          status: 'reactivation-required',
          vat: availability.vat ?? subscriptionVat,
          revokedAtMicros: availability.revokedAtMicros,
        };
      }
      if (availability.state !== 'AVAILABLE' || !availability.canSubscribe) {
        return {
          status: 'error',
          message: t(getSubscriptionUnavailableReasonKey(availability.unavailableReason)),
        };
      }

      await subscribeCustomer(subscriptionVat, effectiveCustomerId);
      await refreshAfterMutation();
      showActiveSubscriptionNotification('subscribed', subscriptionVat);
      return { status: 'success' };
    } catch (error) {
      console.error('Error adding customer VAT subscription:', error);
      const latestAvailability = await getLatestSubscriptionAvailability(subscriptionVat);
      if (latestAvailability?.state === 'ACTIVE') {
        await refreshAfterStateChange();
        showActiveSubscriptionNotification('subscribed', latestAvailability.vat ?? subscriptionVat);
        return { status: 'success' };
      }
      if (latestAvailability?.state === 'REVOKED' && latestAvailability.canReactivate) {
        await refreshAfterStateChange();
        return {
          status: 'reactivation-required',
          vat: latestAvailability.vat ?? subscriptionVat,
          revokedAtMicros: latestAvailability.revokedAtMicros,
        };
      }
      if (latestAvailability && (latestAvailability.state !== 'AVAILABLE' || !latestAvailability.canSubscribe)) {
        await refreshAfterStateChange();
        return { status: 'error', message: getStateChangedMessage(latestAvailability) };
      }
      await refreshSubscriptionsSafely();
      return { status: 'error', message: t('etransport.subscriptions.feedback.actionerror') };
    }
  }

  async function handleRevoke(subscriptionVat: string): Promise<SubscriptionActionResult> {
    if (effectiveCustomerId === undefined) {
      return { status: 'error', message: t('etransport.subscriptions.feedback.revokeerror') };
    }
    resetFeedback();
    const request: ETransportCustomerSubscriptionRequestDto = {
      vat: subscriptionVat,
      customerId: effectiveCustomerId,
    };
    try {
      if (isAdmin) {
        await adminRevokeMutation.mutateAsync({ data: request });
      } else {
        await revokeMutation.mutateAsync({ data: request });
      }
      await refreshAfterMutation();
      showNotification(
        'success',
        t('etransport.subscriptions.feedback.revoked', {
          customer: selectedCustomerLabel ?? String(effectiveCustomerId),
          vat: subscriptionVat,
        }),
        undefined,
        10
      );
      return { status: 'success' };
    } catch (error) {
      console.error('Error revoking customer VAT subscription:', error);
      const statusCode =
        error instanceof PlutoClientException
          ? error.statusCode
          : axios.isAxiosError(error)
            ? error.response?.status
            : undefined;
      if (statusCode === 409) {
        await refreshSubscriptions();
        return { status: 'protected', message: t('etransport.subscriptions.feedback.revokeerror') };
      }
      return { status: 'error', message: t('etransport.subscriptions.feedback.actionerror') };
    }
  }

  function handleCustomerChange(value: CustomerOption | null) {
    resetFeedback();
    onCustomerChange(value?.id);
  }

  function handleCloseAddSubscription() {
    setAddSubscriptionOpen(false);
    requestAnimationFrame(() => addButtonRef.current?.focus());
  }

  const subscriptions = subscriptionsQuery.data?.data ?? [];
  const visibleSubscriptions = filterSubscriptions(subscriptions, subscriptionSearch, statusFilter);
  const selectedCustomerLabel = selectedCustomer ? `${selectedCustomer.name}` : undefined;
  const customerNoLongerAccessible =
    !isLoadingCustomers && !customersLoadError && selectedCustomerId !== undefined && selectedCustomer === undefined;
  const showCustomerOnCards = customers.length > 1;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: isPhoneOrHighScreen() ? '100%' : '100vh',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1, p: 1 }}>
        <IconButton
          autoFocus
          onClick={onClose}
          aria-label={t('etransport.subscriptions.close')}
          data-qa-element-id="etr_subscriptions_close"
        >
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5 }}>
            <Typography variant="h6">{t('etransport.subscriptions.title')}</Typography>
            <IconButton
              size="small"
              onClick={() => setInfoDialogOpen(true)}
              aria-label={t('etransport.subscriptions.info.title')}
              data-qa-element-id="etr_subscriptions_info"
            >
              <InfoIcon sx={{ fontSize: '1.5rem', color: 'text.secondary' }} />
            </IconButton>
          </Stack>
          {selectedCustomerLabel && (
            <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
              {selectedCustomerLabel}
            </Typography>
          )}
        </Box>
      </Stack>
      <Divider />

      <Dialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="etransport-subscriptions-info-title"
      >
        <DialogTitle id="etransport-subscriptions-info-title" component="h6" sx={{ pr: 6 }}>
          {t('etransport.subscriptions.info.title')}
        </DialogTitle>
        <IconButton
          aria-label={t('general.close')}
          onClick={() => setInfoDialogOpen(false)}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
          data-qa-element-id="etr_subscriptions_info_close"
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <Stack sx={{ gap: 2 }}>
            <Typography>{t('etransport.subscriptions.info.introduction')}</Typography>
            <Box component="ol" sx={{ pl: 3, m: 0, display: 'grid', gap: 1 }}>
              <Typography component="li" variant="body1">
                {t('etransport.subscriptions.info.noToken')}
              </Typography>
              <Typography component="li" variant="body1">
                {t('etransport.subscriptions.info.otherCustomerToken')}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialogOpen(false)}>{t('general.close')}</Button>
        </DialogActions>
      </Dialog>

      <Stack sx={{ gap: 2, p: 2, pb: 12, overflowY: 'auto' }}>
        <Typography variant="body2" color="text.secondary">
          {t('etransport.subscriptions.description')}
        </Typography>

        <Autocomplete
          options={customers}
          value={selectedCustomer ?? null}
          loading={isLoadingCustomers}
          getOptionLabel={(option) => option.name ?? ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_, value) => handleCustomerChange(value)}
          onClose={(_, reason) => {
            if (reason === 'selectOption') (document.activeElement as HTMLElement)?.blur();
          }}
          disabled={isLoadingCustomers || customersLoadError || isMutating}
          slotProps={{ paper: { elevation: 4 } }}
          renderInput={(params) => (
            <TextField {...params} label={t('etransport.subscriptions.customer')} size="small" />
          )}
          data-qa-element-id="etr_subscriptions_customer"
        />

        {customersLoadError && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={onRetryCustomers}>
                {t('etransport.cardview.retry')}
              </Button>
            }
          >
            {t('etransport.freightfrom.apierror')}
          </Alert>
        )}

        {!isLoadingCustomers && !customersLoadError && customers.length === 0 && (
          <Alert severity="warning">{t('etransport.subscriptions.nocustomers')}</Alert>
        )}

        {customerNoLongerAccessible && (
          <Alert severity="warning">{t('etransport.subscriptions.customerunavailable')}</Alert>
        )}

        {!isLoadingCustomers &&
          !customersLoadError &&
          !customerNoLongerAccessible &&
          selectedCustomer === undefined &&
          customers.length > 0 && <Alert severity="info">{t('etransport.cardview.selectcustomer')}</Alert>}

        {actionError && <Alert severity="error">{actionError}</Alert>}

        {effectiveCustomerId !== undefined && (
          <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ gap: 1 }}>
            <TextField
              value={subscriptionSearch}
              onChange={(event) => setSubscriptionSearch(event.target.value)}
              label={t('etransport.subscriptions.filters.vat')}
              size="small"
              fullWidth
              slotProps={{ htmlInput: { 'data-qa-element-id': 'etr_subscriptions_vat_filter' } }}
            />
            <TextField
              select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as SubscriptionStatusFilter)}
              label={t('etransport.subscriptions.filters.status')}
              size="small"
              sx={{ minWidth: 180 }}
              data-qa-element-id="etr_subscriptions_status_filter"
            >
              <MenuItem value="all">{t('etransport.subscriptions.filters.all')}</MenuItem>
              <MenuItem value="active">{t('etransport.subscriptions.status.active')}</MenuItem>
              <MenuItem value="revoked">{t('etransport.subscriptions.status.revoked')}</MenuItem>
            </TextField>
          </Stack>
        )}
        {subscriptionsQuery.isLoading && (
          <Stack sx={{ alignItems: 'center', gap: 1, py: 4 }}>
            <CircularProgress size={30} />
            <Typography>{t('etransport.subscriptions.loading')}</Typography>
          </Stack>
        )}

        {subscriptionsQuery.isError && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => void subscriptionsQuery.refetch()}>
                {t('etransport.cardview.retry')}
              </Button>
            }
          >
            {t('etransport.subscriptions.loaderror')}
          </Alert>
        )}

        {!subscriptionsQuery.isLoading &&
          !subscriptionsQuery.isError &&
          effectiveCustomerId !== undefined &&
          subscriptions.length === 0 && <Alert severity="info">{t('etransport.subscriptions.empty')}</Alert>}

        {!subscriptionsQuery.isLoading &&
          !subscriptionsQuery.isError &&
          subscriptions.length > 0 &&
          visibleSubscriptions.length === 0 && (
            <Alert severity="info">{t('etransport.subscriptions.filters.noresults')}</Alert>
          )}

        <Stack sx={{ gap: 1 }}>
          {selectedCustomer &&
            visibleSubscriptions.map((subscription) => (
              <SubscriptionCard
                key={`${subscription.customerId}-${subscription.vat}`}
                subscription={subscription}
                customer={selectedCustomer}
                customerLabel={showCustomerOnCards ? selectedCustomerLabel : undefined}
                isMutating={isMutating}
                onReactivate={handleReactivate}
                onRevoke={handleRevoke}
              />
            ))}
        </Stack>
      </Stack>

      {!addSubscriptionOpen && (
        <Fab
          ref={addButtonRef}
          variant="extended"
          color="secondary"
          onClick={() => {
            resetFeedback();
            setAddSubscriptionOpen(true);
          }}
          disabled={selectedCustomer === undefined || isMutating}
          aria-label={t('etransport.subscriptions.add')}
          data-qa-element-id="etr_subscriptions_add"
          sx={{
            position: 'absolute',
            right: isPhoneOrHighScreen() ? 10 : 18,
            bottom: isPhoneOrHighScreen() ? 5 : 20,
            backgroundColor: 'var(--mui-palette-secondary-main)',
            color: 'white',
            border: '2px solid white',
            '&:hover': {
              backgroundColor: 'var(--mui-palette-secondary-light)',
            },
            '&.Mui-disabled': {
              backgroundColor: 'grey',
              color: 'white',
              opacity: 1,
            },
          }}
        >
          <AddIcon sx={{ mr: 1 }} />
          {t('etransport.subscriptions.add')}
        </Fab>
      )}

      {addSubscriptionOpen && selectedCustomer && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 10,
            backgroundColor: 'var(--mui-palette-contrast-background)',
            animation: 'slideInFromLeft 0.4s ease-out',
          }}
          data-qa-element-id="etr_add_subscription_panel"
        >
          <AddSubscriptionForm
            customer={selectedCustomer}
            isSubmitting={isMutating}
            error={actionError}
            onClose={handleCloseAddSubscription}
            onSubmit={handleAddSubscription}
            onReactivate={handleReactivate}
          />
        </Box>
      )}
    </Box>
  );
}
